import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import { ErrorBanner, Select } from "../../components/crm/Form";
import api, { formatApiError } from "../../lib/api";

const PLATFORM_OPTIONS = [
  { value: "enphase", label: "Enphase" },
  { value: "solaredge", label: "SolarEdge" },
  { value: "tesla", label: "Tesla" },
  { value: "eg4", label: "EG4" },
  { value: "generic", label: "Generic" },
];

export default function WarrantiesList() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const equipmentId = params.get("equipment_id") || "";
  const platform = params.get("platform_type") || "";

  const [list, setList] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const filters = {};
      if (equipmentId) filters.equipment_id = equipmentId;
      if (platform) filters.platform_type = platform;
      const { data } = await api.get("/warranties/", { params: filters });
      setList(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentId, platform]);

  const setPlatform = (val) => {
    const next = new URLSearchParams(params);
    if (val) next.set("platform_type", val);
    else next.delete("platform_type");
    setParams(next, { replace: true });
  };

  return (
    <Layout title="Warranties" subtitle="CRM">
      <EntityHeader
        overline="coverage"
        title="Warranties"
        subtitle="All warranty records across every tracked piece of equipment."
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-44">
          <Select
            value={platform}
            onChange={(v) => setPlatform(v)}
            options={PLATFORM_OPTIONS}
            placeholder="All platforms"
            data-testid="warranties-platform-filter"
          />
        </div>
        {equipmentId && (
          <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm">
            filter · equipment_id {equipmentId.slice(0, 8)}
          </div>
        )}
      </div>

      <ErrorBanner message={err} />

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="warranties-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
              <th className="text-left px-5 py-3 font-bold">Manufacturer</th>
              <th className="text-left px-5 py-3 font-bold">Platform</th>
              <th className="text-left px-5 py-3 font-bold">Coverage</th>
              <th className="text-left px-5 py-3 font-bold">Expires</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!loading && list.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-5 py-12 text-center text-sm text-slate-500"
                  data-testid="warranties-empty"
                >
                  No warranties yet.
                </td>
              </tr>
            )}
            {list.map((w) => (
              <tr
                key={w.id}
                className="cursor-pointer hover:bg-slate-50/60"
                onClick={() => nav(`/warranties/${w.id}`)}
                data-testid={`warranty-row-${w.id}`}
              >
                <td className="px-5 py-3 font-medium text-slate-900">
                  {w.manufacturer}
                </td>
                <td className="px-5 py-3 font-data text-xs uppercase tracking-[0.1em] text-slate-700">
                  {w.platform_type || "—"}
                </td>
                <td className="px-5 py-3 font-data text-xs uppercase tracking-[0.1em] text-slate-700">
                  {w.coverage_type}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700 tabular">
                  {w.expires}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
